import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { ConfigCreateOverlayProps } from '../../types';
import { useStyles } from '../../styled';
import {
  Backdrop,
  Box,
  MuiBox,
  SvgIconButton,
  DotProgress,
  Logo,
  ContainerBox,
  Grid,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { CreateKioskForm } from './CreateKioskForm';
import { CreateConfigurationForm } from './CreateConfigurationForm';
import { CreateClientIntegrationForm } from './CreateClientIntegrationForm';
import { CreateKioskHardwareForm } from './CreateKioskHardwareForm';
import { CreateCustomerForm } from './CreateCustomerForm';
import { actions as areaActions, selectors as areaSelectors } from 'store/areas';
import { actions as ciActions, selectors as ciSelectors } from 'store/clientIntegrations';
import { actions as kiosksActions, selectors as kiosksSelectors } from 'store/kiosks';
import { actions as configurationsActions, selectors as configurationsSelectors } from 'store/configurations';
import { actions as kioskHardwareActions, selectors as kioskHardwareSelectors } from 'store/kioskHardware';

export const ConfigCreateOverlay = (props: ConfigCreateOverlayProps) => {
  const { kiosk, selectedCI, onCancel, configType } = props;

  // ACTION CREATORS AND SELECTORS
  const resetAreaError = useActions(areaActions.resetError);
  const [createCI, createCustomer, resetCIError, resetCIAction] = useActions([
    ciActions.createClientIntegration,
    ciActions.createCustomer,
    ciActions.resetError,
    ciActions.resetAction
  ]);
  const [createKiosk, resetKioskError, resetKioskAction] = useActions([
    kiosksActions.createKiosk,
    kiosksActions.resetError,
    kiosksActions.resetAction
  ]);
  const [createConfig, resetConfigError, resetConfigAction] = useActions([
    configurationsActions.createConfiguration,
    configurationsActions.resetError,
    configurationsActions.resetAction
  ]);
  const [createKioskHardware, resetKioskHardwareError, resetKioskHardwareAction] = useActions([
    kioskHardwareActions.createKioskHardware,
    kioskHardwareActions.resetError,
    kioskHardwareActions.resetAction
  ]);

  const { pending: areaPending, error: areaError } = useSelector(areaSelectors.selectAction);
  const { success: ciSuccess, pending: ciPending, error: ciError } = useSelector(ciSelectors.selectAction);
  const customer = useSelector(ciSelectors.selectCustomer);
  const { success: kioskSuccess, pending: kioskPending, error: kioskError } = useSelector(kiosksSelectors.selectAction);
  const {
    success: configSuccess,
    pending: configPending,
    error: configError
  } = useSelector(configurationsSelectors.selectAction);
  const {
    success: kioskHardwareSuccess,
    pending: kioskHardwarePending,
    error: kioskHardwareError
  } = useSelector(kioskHardwareSelectors.selectAction);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const classes = useStyles()();

  const resetError = () => {
    if (error) {
      setError('');
      if (ciError) resetCIError();
      if (kioskError) resetKioskError();
      if (configError) resetConfigError();
      if (kioskHardwareError) resetKioskHardwareError();
      if (areaError) resetAreaError();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (payload: any) => {
    if (configType === 'clientIntegration') createCI(payload);
    if (configType === 'kiosk') createKiosk(payload);
    if (configType === 'configuration') createConfig(payload);
    if (configType === 'kioskHardware') createKioskHardware(payload);
    if (configType === 'customer') createCustomer(payload);
  };

  useEffect(() => {
    if (ciPending || kioskPending || configPending || kioskHardwarePending || areaPending) {
      setPending(true);
    } else {
      setPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciPending, kioskPending, configPending, areaPending, kioskHardwarePending]);

  useEffect(() => {
    if (ciError) setError(ciError);
    if (kioskError) setError(kioskError);
    if (configError) setError(configError);
    if (kioskHardwareError) setError(kioskHardwareError);
    if (areaError) setError(areaError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciError, kioskError, configError, areaError, kioskHardwareError]);

  useEffect(() => {
    if (ciSuccess || customer) {
      resetCIAction();
      onCancel();
    }
    if (kioskSuccess) {
      resetKioskAction();
      onCancel();
    }
    if (configSuccess) {
      resetConfigAction();
      onCancel();
    }
    if (kioskHardwareSuccess) {
      resetKioskHardwareAction();
      onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciSuccess, kioskSuccess, configSuccess, kioskHardwareSuccess, customer]);

  return (
    <>
      {pending && (
        <Backdrop open={pending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      <Backdrop open>
        <Box bg="light-gray" height="100vh" width="100%" overflow="auto">
          <ContainerBox className={classes.createNewClientContainer}>
            <Box bg="white" sx={{ borderRadius: '32px 32px 0 0' }} height="100%">
              <Grid className={classes.createNewClientLogoBlock}>
                <MuiBox className={classes.createNewClientLogo}>
                  <Logo />
                </MuiBox>
                <MuiBox className={classes.createNewClientCloseIcon}>
                  <SvgIconButton appearance="transparent" onClick={onCancel}>
                    <SvgIcon name="exit_circle" />
                  </SvgIconButton>
                </MuiBox>
              </Grid>
              {configType === 'clientIntegration' && (
                <CreateClientIntegrationForm
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
              {configType === 'kiosk' && (
                <CreateKioskForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'configuration' && (
                <CreateConfigurationForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'kioskHardware' && (
                <CreateKioskHardwareForm
                  kiosk={kiosk}
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
              {configType === 'customer' && (
                <CreateCustomerForm
                  selectedCI={selectedCI}
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
            </Box>
          </ContainerBox>
        </Box>
      </Backdrop>
    </>
  );
};
